import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { usePageSearch, matchesSearch, noMatchText } from '../lib/pageSearch';
import { getWorkloadDashboard, updatePMCapacity } from '../api';
import LoadingState from '../components/LoadingState';
import StateScreen from '../components/ui/StateScreen';
import SegmentedControl from '../components/ui/SegmentedControl';
import { Avatar } from '../components/ui';

/* ═══════════════════════════════════════════════════════════════════════════
   Team Workload page — warm-cream + sage redesign
   Top-level role-gated route (/workload): capacity planning across PMs —
   who's overloaded, who's free soon, contract value carried, per-PM drill-down
   with a value/project drawdown chart. Token-only colors. Visual reskin —
   API + filter/sort logic unchanged.
   ═══════════════════════════════════════════════════════════════════════════ */

/* full figure, cents — everywhere except the genuinely tight spots */
const fmt$ = (n) => {
  if (n == null || Number.isNaN(+n)) return '—';
  return (+n).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
/* compact $X.XM / $Xk — only the capacity fraction, Under-Mgmt tile, chart axis */
const fmt$c = (n) => {
  if (n == null || Number.isNaN(+n)) return '—';
  const a = Math.abs(+n);
  if (a >= 1e6) {
    const m = a / 1e6;
    return '$' + (m >= 100 ? m.toFixed(0) : m.toFixed(1)) + 'M';
  }
  if (a >= 1e3) return '$' + Math.round(a / 1e3) + 'k';
  return '$' + Math.round(a);
};
const fmtDate = (d) => {
  if (!d) return '—';
  try {
    const dt = new Date(d + 'T00:00:00');
    if (Number.isNaN(dt.getTime())) return d;
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return d;
  }
};
const fmtMonth = (ym) => {
  if (!ym) return '';
  const parts = String(ym).split('-');
  if (parts.length < 2) return ym;
  return `${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][+parts[1] - 1]} '${parts[0].slice(2)}`;
};
const initialsOf = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
};
const daysLabel = (n) => {
  if (n <= 0) return 'now';
  if (n === 1) return '1 day';
  if (n < 14) return `${n} days`;
  if (n < 30) return `${Math.round(n / 7)} weeks`;
  if (n < 60) return '1 month';
  return `${Math.round(n / 30)} months`;
};

const ring = (c) => `color-mix(in oklch, ${c} 22%, transparent)`;
const utilTone = (r) => (r >= 1 ? 'var(--danger)' : r >= 0.8 ? 'var(--warning)' : 'var(--success)');
const scoreTone = (n) => (n >= 70 ? 'var(--danger)' : n >= 40 ? 'var(--warning)' : 'var(--success)');
const urgencyBucket = (d) => (d <= 0 ? 'now' : d <= 14 ? 'soon' : 'later');

/* ─────────────── Inline SVG icons ─────────────── */

const Icon = ({ name, size = 16, sw = 1.6 }) => {
  const s = size;
  const p = (d) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
      {d}
    </svg>
  );
  switch (name) {
    case 'refresh':
      return p(
        <>
          <path d="M3 12a9 9 0 0 1 15.5-6.3L21 8" />
          <path d="M21 3v5h-5" />
          <path d="M21 12a9 9 0 0 1-15.5 6.3L3 16" />
          <path d="M3 21v-5h5" />
        </>
      );
    case 'clock':
      return p(
        <>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 8v4l3 2" />
        </>
      );
    case 'alert':
      return p(
        <>
          <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
          <path d="M12 9v4" />
          <circle cx="12" cy="17" r="0.9" fill="currentColor" stroke="none" />
        </>
      );
    case 'chevron-d':
      return p(<path d="m6 9 6 6 6-6" />);
    default:
      return null;
  }
};

const MicroLabel = ({ children }) => (
  <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-3)' }}>{children}</div>
);

/* ─────────────── Action row ─────────────── */

const ActionRow = ({ onRefresh }) => (
  <header style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '0 2px' }}>
    <p style={{ flex: 1, minWidth: 0, margin: 0, fontSize: 15, color: 'var(--text-3)', letterSpacing: '-0.005em', lineHeight: 1.45 }}>
      Capacity planning and workload distribution across project managers
    </p>
    <button
      type="button"
      className="w-refresh"
      onClick={onRefresh}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        height: 32,
        padding: '0 12px',
        borderRadius: 8,
        fontSize: 14,
        fontWeight: 500,
        color: 'var(--text-2)',
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-default)',
        boxShadow: 'var(--shadow-xs)',
        cursor: 'pointer',
        flex: '0 0 auto',
      }}
    >
      <Icon name="refresh" size={13} /> Refresh
    </button>
  </header>
);

/* ─────────────── KPI row ─────────────── */

const KpiTile = ({ label, value, sub, valueColor, muted, valueSize = 28 }) => (
  <div className="wkr-tile" style={{ display: 'flex', flexDirection: 'column', gap: 9, flex: '1 1 160px', minWidth: 140, padding: '16px 20px' }}>
    <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '0.075em', textTransform: 'uppercase', color: 'var(--text-3)' }}>{label}</div>
    <div className="mono" style={{ fontSize: valueSize, fontWeight: 800, lineHeight: 1.08, letterSpacing: '-0.03em', fontVariantNumeric: 'tabular-nums', minWidth: 0, overflowWrap: 'anywhere', color: muted ? 'var(--text-4)' : (valueColor || 'var(--text-1)') }}>
      {value}
    </div>
    <div style={{ marginTop: 'auto', minHeight: 16, fontSize: 12, lineHeight: 1.35, color: 'var(--text-3)' }}>{sub || ' '}</div>
  </div>
);

const KpiDivider = () => <div className="wkr-div" aria-hidden="true" style={{ width: 1, alignSelf: 'stretch', background: 'var(--border-subtle)', margin: '14px 0', flex: '0 0 auto' }} />;

const KpiRow = ({ summary }) => {
  const s = summary;
  const needSoon = s.needWorkSoon || 0;
  // "Quiet ledger" (matches Job Costs): one calm surface panel, values neutral —
  // color reserved for the single figure that carries meaning (PMs freeing up soon).
  return (
    <section className="wkr" role="group" aria-label="Team workload totals" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'stretch', gap: 0, padding: '4px 8px', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-sm)' }}>
      <KpiTile label="Active PMs" value={(s.totalActivePMs || 0).toLocaleString('en-US')} muted={!s.totalActivePMs} />
      <KpiDivider />
      <KpiTile label="Active Projects" value={(s.totalActiveProjects || 0).toLocaleString('en-US')} muted={!s.totalActiveProjects} />
      <KpiDivider />
      <KpiTile label="Open Tasks" value={(s.totalActiveTasks || 0).toLocaleString('en-US')} muted={!s.totalActiveTasks} />
      <KpiDivider />
      <KpiTile label="Need Work Soon" value={needSoon.toLocaleString('en-US')} sub="within 14 days" valueColor={needSoon ? 'var(--warning)' : undefined} muted={!needSoon} />
      <KpiDivider />
      <KpiTile label="Total Under Mgmt" value={fmt$c(s.totalValueUnderMgmt)} sub="contract value" muted={!s.totalValueUnderMgmt} />
    </section>
  );
};

/* ─────────────── Filter + sort bar ─────────────── */

const SORT_OPTIONS = [
  { value: 'workload', label: 'Workload Score' },
  { value: 'soonest', label: 'Soonest Available' },
  { value: 'projects', label: 'Most Projects' },
  { value: 'name', label: 'Name' },
];

const FilterSortBar = ({ filterUrgency, onFilter, counts, sortBy, onSort }) => (
  <section style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', padding: '10px 12px', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-sm)' }}>
    <SegmentedControl
      value={filterUrgency}
      onChange={onFilter}
      options={[
        { value: 'all', label: 'All', count: counts.all },
        { value: 'now', label: 'Needs Work Now', count: counts.now },
        { value: 'soon', label: 'Free Within 14d', count: counts.soon },
        { value: 'later', label: 'Busy 14d+', count: counts.later },
      ]}
    />
    <div style={{ flex: 1, minWidth: 8 }} />
    <div style={{ display: 'flex', alignItems: 'center', gap: 9, flex: '0 0 auto' }}>
      <span style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--text-3)' }}>Sort by</span>
      <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
        <select
          value={sortBy}
          onChange={(e) => onSort(e.target.value)}
          className="w-sort"
          style={{
            appearance: 'none',
            WebkitAppearance: 'none',
            height: 32,
            padding: '0 32px 0 12px',
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 500,
            color: 'var(--text-1)',
            fontFamily: 'var(--font-sans)',
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-default)',
            boxShadow: 'var(--shadow-xs)',
            cursor: 'pointer',
            letterSpacing: '-0.005em',
          }}
        >
          {SORT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <span style={{ position: 'absolute', right: 10, color: 'var(--text-3)', pointerEvents: 'none', display: 'inline-flex' }}>
          <Icon name="chevron-d" size={14} />
        </span>
      </div>
    </div>
  </section>
);

/* ─────────────── Capacity bar (editable cap) ─────────────── */

const CapacityBar = ({ pm, onSetCap }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef(null);
  const cap = pm.capacityCap;
  const ratio = cap ? pm.totalUnderMgmt / cap : 0;
  const over = ratio > 1;
  const tone = cap ? utilTone(ratio) : 'var(--text-4)';
  const fillW = cap ? Math.min(ratio, 1) * 100 : 0;

  const stop = (e) => e.stopPropagation();
  const beginEdit = (e) => {
    e.stopPropagation();
    setDraft(cap ? (cap / 1e6).toFixed(1) : '');
    setEditing(true);
    setTimeout(() => inputRef.current && inputRef.current.focus(), 0);
  };
  const commit = () => {
    const v = parseFloat(draft);
    if (!Number.isNaN(v) && v > 0) onSetCap(pm.name, Math.round(v * 1e6));
    setEditing(false);
  };

  return (
    <div onClick={stop} style={{ display: 'flex', flexDirection: 'column', gap: 7, minWidth: 0 }}>
      <MicroLabel>Capacity</MicroLabel>
      <div className="mono" style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 14.5, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em', whiteSpace: 'nowrap' }}>
        <span style={{ fontWeight: 700, color: 'var(--text-1)' }}>{fmt$c(pm.totalUnderMgmt)}</span>
        <span style={{ color: 'var(--text-4)' }}>/</span>
        {editing ? (
          <span onClick={stop} style={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
            <span style={{ color: 'var(--text-3)' }}>$</span>
            <input
              ref={inputRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commit();
                if (e.key === 'Escape') setEditing(false);
              }}
              onBlur={commit}
              inputMode="decimal"
              type="number"
              className="mono w-cap-input"
              style={{
                width: 44,
                height: 22,
                padding: '0 4px',
                borderRadius: 6,
                textAlign: 'right',
                fontSize: 14,
                fontFamily: 'var(--font-mono)',
                fontVariantNumeric: 'tabular-nums',
                color: 'var(--text-1)',
                background: 'var(--bg-surface)',
                border: '1px solid var(--accent)',
                boxShadow: '0 0 0 3px var(--accent-ring)',
                outline: 'none',
              }}
            />
            <span style={{ color: 'var(--text-3)' }}>M</span>
          </span>
        ) : (
          <button
            type="button"
            className="w-cap-edit"
            onClick={beginEdit}
            title="Set capacity cap"
            style={{
              fontFamily: 'var(--font-mono)',
              fontVariantNumeric: 'tabular-nums',
              fontSize: 14.5,
              fontWeight: 600,
              color: cap ? 'var(--accent-text)' : 'var(--text-3)',
              background: 'transparent',
              border: 0,
              padding: '0 1px',
              cursor: 'pointer',
              borderBottom: '1px dashed color-mix(in oklch, var(--accent) 45%, transparent)',
            }}
          >
            {cap ? fmt$c(cap) : 'Set cap'}
          </button>
        )}
      </div>
      <div style={{ height: 7, borderRadius: 999, background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)', overflow: 'hidden', position: 'relative' }}>
        {!!cap && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              width: `${fillW}%`,
              borderRadius: 999,
              background: over
                ? 'repeating-linear-gradient(45deg, var(--danger) 0 5px, color-mix(in oklch, var(--danger) 62%, var(--bg-surface)) 5px 10px)'
                : tone,
            }}
          />
        )}
      </div>
      <div className="mono" style={{ fontSize: 12.5, fontVariantNumeric: 'tabular-nums', fontWeight: 500, color: over ? 'var(--danger)' : cap ? 'var(--text-3)' : 'var(--text-4)' }}>
        {cap ? (over ? `Over by ${fmt$c(pm.totalUnderMgmt - cap)}` : `${Math.round(ratio * 100)}% utilized`) : 'No cap set'}
      </div>
    </div>
  );
};

/* ─────────────── Progress ring + score bar + urgency badge ─────────────── */

const ProgressRing = ({ done, total }) => {
  const pct = total ? done / total : 0;
  const R = 15;
  const C = 2 * Math.PI * R;
  const color = pct >= 0.66 ? 'var(--success)' : 'var(--accent)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
      <div style={{ position: 'relative', width: 38, height: 38, flex: '0 0 auto' }}>
        <svg width="38" height="38" viewBox="0 0 38 38" style={{ transform: 'rotate(-90deg)' }}>
          <circle cx="19" cy="19" r={R} fill="none" stroke="var(--bg-surface-2)" strokeWidth="4" />
          <circle cx="19" cy="19" r={R} fill="none" stroke={color} strokeWidth="4" strokeLinecap="round" strokeDasharray={C} strokeDashoffset={C * (1 - pct)} />
        </svg>
        <span className="mono" style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: 'var(--text-2)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.03em' }}>
          {Math.round(pct * 100)}
        </span>
      </div>
      <div className="mono" style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--text-1)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em', whiteSpace: 'nowrap' }}>
        {done}
        <span style={{ color: 'var(--text-4)' }}>/{total}</span>
      </div>
    </div>
  );
};

const ScoreBar = ({ score }) => {
  const tone = scoreTone(score);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 7, minWidth: 0 }}>
      <div className="mono" style={{ fontSize: 16.5, fontWeight: 800, color: tone, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.03em', lineHeight: 1 }}>{score}</div>
      <div style={{ height: 6, borderRadius: 999, background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${Math.min(score, 100)}%`, borderRadius: 999, background: tone }} />
      </div>
    </div>
  );
};

const MetricCell = ({ label, value }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 }}>
    <MicroLabel>{label}</MicroLabel>
    <div className="mono" style={{ fontSize: 25.5, fontWeight: 800, color: 'var(--text-1)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.03em', lineHeight: 1 }}>{value}</div>
  </div>
);

const UrgencyBadge = ({ pm }) => {
  const d = pm.daysUntilFree;
  const needs = d <= 0;
  const tone = needs ? 'var(--danger)' : d <= 7 ? 'var(--warning)' : d <= 30 ? 'var(--accent)' : 'var(--success)';
  const soft = needs ? 'var(--danger-soft)' : d <= 7 ? 'var(--warning-soft)' : d <= 30 ? 'var(--accent-soft)' : 'var(--success-soft)';
  const fg = needs ? 'var(--danger)' : d <= 7 ? 'var(--warning)' : d <= 30 ? 'var(--accent-text)' : 'var(--success)';
  const text = needs ? 'Needs Work' : `Free in ${daysLabel(d)}`;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, height: 21, padding: '0 9px', borderRadius: 999, fontSize: 13, fontWeight: 600, color: fg, background: soft, border: `1px solid ${ring(tone)}`, whiteSpace: 'nowrap' }}>
        <Icon name={needs ? 'alert' : 'clock'} size={12} sw={2} />
        {text}
      </span>
      {pm.overdueTasks > 0 && (
        <span className="mono" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, height: 21, padding: '0 8px', borderRadius: 999, fontSize: 12.5, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: 'var(--danger)', background: 'var(--danger-soft)', border: `1px solid ${ring('var(--danger)')}`, whiteSpace: 'nowrap' }}>
          {pm.overdueTasks} overdue
        </span>
      )}
    </div>
  );
};

/* ─────────────── Drawdown chart (dual-axis SVG) ─────────────── */

const DrawdownChart = ({ drawdown }) => {
  if (!drawdown || drawdown.length === 0) {
    return <div style={{ padding: 16, textAlign: 'center', fontSize: 13.5, color: 'var(--text-4)' }}>No timeline data available</div>;
  }
  const W = 760;
  const H = 224;
  const padL = 14;
  const padR = 54;
  const padT = 26;
  const padB = 30;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;
  const n = drawdown.length;
  const step = plotW / n;
  const barW = Math.min(30, step * 0.42);

  const maxP = Math.max(...drawdown.map((d) => d.projects), 1);
  const rawMaxV = Math.max(...drawdown.map((d) => d.value), 1);
  const niceMaxV = Math.max(Math.ceil(rawMaxV / 2e6) * 2e6, 2e6);

  const cx = (i) => padL + step * (i + 0.5);
  const barH = (val) => (val / maxP) * plotH * 0.82;
  const vy = (v) => padT + plotH - (v / niceMaxV) * plotH;
  const grid = [0, 1, 2, 3, 4];
  const linePts = drawdown.map((d, i) => `${cx(i)},${vy(d.value)}`).join(' ');

  return (
    <div style={{ overflowX: 'auto', overflowY: 'hidden' }}>
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ display: 'block', minWidth: W }}>
        {grid.map((g) => {
          const y = padT + (plotH * g) / 4;
          const v = niceMaxV * (1 - g / 4);
          return (
            <g key={g}>
              <line x1={padL} y1={y} x2={padL + plotW} y2={y} stroke="var(--border-subtle)" strokeWidth="1" strokeDasharray={g === 4 ? '0' : '2 3'} />
              <text x={padL + plotW + 8} y={y + 3.5} className="mono" fontSize="9.5" fill="var(--text-4)" style={{ fontVariantNumeric: 'tabular-nums' }}>
                {fmt$c(v)}
              </text>
            </g>
          );
        })}
        {drawdown.map((d, i) => {
          const h = barH(d.projects);
          const x = cx(i) - barW / 2;
          const y = padT + plotH - h;
          return (
            <g key={`b${i}`}>
              <rect x={x} y={y} width={barW} height={h} rx="3" fill="color-mix(in oklch, var(--accent) 22%, transparent)" stroke="color-mix(in oklch, var(--accent) 32%, transparent)" strokeWidth="1" />
              <text x={cx(i)} y={y - 6} textAnchor="middle" className="mono" fontSize="10" fontWeight="600" fill="var(--accent-text)" style={{ fontVariantNumeric: 'tabular-nums' }}>
                {d.projects}
              </text>
            </g>
          );
        })}
        <polyline points={linePts} fill="none" stroke="var(--success)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {drawdown.map((d, i) => (
          <circle key={`d${i}`} cx={cx(i)} cy={vy(d.value)} r="3.2" fill="var(--bg-surface)" stroke="var(--success)" strokeWidth="2" />
        ))}
        {drawdown.map((d, i) => (
          <text key={`m${i}`} x={cx(i)} y={H - 10} textAnchor="middle" fontSize="10.5" fill="var(--text-3)">
            {fmtMonth(d.month)}
          </text>
        ))}
      </svg>
    </div>
  );
};

const ChartLegend = () => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 13, color: 'var(--text-3)', fontWeight: 500 }}>
      <span style={{ width: 11, height: 11, borderRadius: 3, background: 'color-mix(in oklch, var(--accent) 22%, transparent)', border: '1px solid color-mix(in oklch, var(--accent) 32%, transparent)' }} />
      Projects
    </span>
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 13, color: 'var(--text-3)', fontWeight: 500 }}>
      <span style={{ width: 14, height: 2, borderRadius: 2, background: 'var(--success)' }} />
      Under Mgmt Value
    </span>
  </div>
);

/* ─────────────── Timeline row + breakdown table ─────────────── */

const TimelineStat = ({ label, value, tone }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 120 }}>
    <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-3)' }}>{label}</span>
    <span className="mono" style={{ fontSize: 14.5, fontWeight: 600, color: tone || 'var(--text-1)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em' }}>{value}</span>
  </div>
);

const TimelineRow = ({ pm }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 28, padding: '2px 2px 14px' }}>
    <TimelineStat label="Latest Finish" value={fmtDate(pm.latestFinishDate)} />
    <TimelineStat label="Next Task Starts" value={fmtDate(pm.nextTaskStart)} />
    <TimelineStat label="Upcoming (14d)" value={pm.upcomingTasks} tone={pm.upcomingTasks > 0 ? 'var(--warning)' : 'var(--text-4)'} />
    <TimelineStat label="Not Started" value={pm.notStartedTasks} tone={pm.notStartedTasks > 0 ? 'var(--text-1)' : 'var(--text-4)'} />
  </div>
);

const ProjLogo = ({ row }) =>
  row.logo_url ? (
    <img src={row.logo_url} alt="" style={{ width: 26, height: 26, borderRadius: 6, objectFit: 'cover', flex: '0 0 auto', border: '1px solid var(--border-subtle)' }} />
  ) : (
    <div className="mono" style={{ width: 26, height: 26, borderRadius: 6, flex: '0 0 auto', background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)', color: 'var(--text-3)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11.5, fontWeight: 600, letterSpacing: '-0.02em' }}>
      {initialsOf(row.project_name)}
    </div>
  );

const Th = ({ children, align = 'left', w }) => (
  <th style={{ textAlign: align, padding: '0 12px 9px', width: w, fontSize: 11.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-3)', whiteSpace: 'nowrap' }}>{children}</th>
);

const BreakdownTable = ({ rows, onOpenProject }) => (
  <div style={{ overflowX: 'auto' }}>
    <table style={{ width: '100%', minWidth: 720, borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          <Th>Project</Th>
          <Th align="right">Contract Value</Th>
          <Th align="center" w={64}>Tasks</Th>
          <Th align="center" w={64}>Active</Th>
          <Th align="center" w={72}>Overdue</Th>
          <Th align="center" w={92}>Complete</Th>
          <Th align="right" w={120}>Latest Finish</Th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id} className="w-prow" onClick={() => onOpenProject && onOpenProject(r.slug || r.id)} style={{ cursor: 'pointer', borderTop: '1px solid var(--border-subtle)' }}>
            <td style={{ padding: '10px 12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                <ProjLogo row={r} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                  <span style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--text-1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 260 }}>{r.project_name}</span>
                  {r.project_number && (
                    <span className="mono" style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>#{r.project_number}</span>
                  )}
                </div>
              </div>
            </td>
            <td className="mono" style={{ padding: '10px 12px', textAlign: 'right', fontSize: 14, fontWeight: 600, color: 'var(--text-1)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em', whiteSpace: 'nowrap' }}>{fmt$(r.contract_value)}</td>
            <td className="mono" style={{ padding: '10px 12px', textAlign: 'center', fontSize: 14, color: 'var(--text-2)', fontVariantNumeric: 'tabular-nums' }}>{r.totalTasks}</td>
            <td className="mono" style={{ padding: '10px 12px', textAlign: 'center', fontSize: 14, color: 'var(--text-2)', fontVariantNumeric: 'tabular-nums' }}>{r.activeTasks}</td>
            <td className="mono" style={{ padding: '10px 12px', textAlign: 'center', fontSize: 14, fontWeight: r.overdueTasks > 0 ? 700 : 400, color: r.overdueTasks > 0 ? 'var(--danger)' : 'var(--text-4)', fontVariantNumeric: 'tabular-nums' }}>{r.overdueTasks}</td>
            <td style={{ padding: '10px 12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
                <div style={{ width: 40, height: 5, borderRadius: 999, background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)', overflow: 'hidden', flex: '0 0 auto' }}>
                  <div style={{ height: '100%', width: `${r.percentComplete}%`, background: 'var(--success)' }} />
                </div>
                <span className="mono" style={{ fontSize: 13, color: 'var(--text-2)', fontVariantNumeric: 'tabular-nums', minWidth: 30, textAlign: 'right' }}>{r.percentComplete}%</span>
              </div>
            </td>
            <td className="mono" style={{ padding: '10px 12px', textAlign: 'right', fontSize: 13.5, color: 'var(--text-2)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{fmtDate(r.latestFinishDate)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/* ─────────────── PM card (header + drawer) ─────────────── */

const PMCard = ({ pm, open, onToggle, onSetCap, onOpenProject }) => (
  <article style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-sm)', overflow: 'hidden' }}>
    <div className="w-pmhead" onClick={onToggle} role="button" aria-expanded={open} style={{ cursor: 'pointer', padding: '16px 18px', overflowX: 'auto' }}>
      <div style={{ display: 'grid', gap: 18, alignItems: 'center', minWidth: 940, gridTemplateColumns: 'minmax(220px,1.25fr) 86px minmax(206px,1.05fr) 134px 74px 138px 30px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          <Avatar initials={initialsOf(pm.name)} size={40} accent />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 }}>
            <span style={{ fontSize: 16.5, fontWeight: 700, color: 'var(--text-1)', letterSpacing: '-0.012em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{pm.name}</span>
            <UrgencyBadge pm={pm} />
          </div>
        </div>
        <MetricCell label="Projects" value={pm.activeProjects} />
        <CapacityBar pm={pm} onSetCap={onSetCap} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
          <MicroLabel>Tasks Done</MicroLabel>
          <ProgressRing done={pm.completedTasks} total={pm.totalTasks} />
        </div>
        <MetricCell label="Active" value={pm.inProgressTasks} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
          <MicroLabel>Workload</MicroLabel>
          <ScoreBar score={pm.workloadScore} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', color: 'var(--text-3)' }}>
          <span style={{ display: 'inline-flex', transition: 'transform 180ms ease', transform: open ? 'rotate(180deg)' : 'none' }}>
            <Icon name="chevron-d" size={18} />
          </span>
        </div>
      </div>
    </div>

    {open && (
      <div style={{ background: 'var(--bg-surface-2)', borderTop: '1px solid var(--border-subtle)', padding: '18px 18px 20px' }}>
        <TimelineRow pm={pm} />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, margin: '2px 2px 10px' }}>
          <span style={{ fontSize: 13.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-3)' }}>Value Drawdown</span>
          <ChartLegend />
        </div>
        <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 10, boxShadow: 'var(--shadow-xs)', padding: '12px 14px', marginBottom: 16 }}>
          <DrawdownChart drawdown={pm.drawdown} />
        </div>
        <span style={{ display: 'block', fontSize: 13.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-3)', margin: '0 2px 8px' }}>Project Breakdown</span>
        <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 10, boxShadow: 'var(--shadow-xs)', padding: '12px 4px 4px' }}>
          <BreakdownTable rows={pm.projectBreakdown || []} onOpenProject={onOpenProject} />
        </div>
      </div>
    )}
  </article>
);

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════════════════════════════════ */

const WorkloadPage = ({ onOpenProject }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterUrgency, setFilterUrgency] = useState('all');
  const [sortBy, setSortBy] = useState('workload');
  const [expandedPM, setExpandedPM] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getWorkloadDashboard();
      setData(result);
    } catch (err) {
      setError(err.message || 'Failed to load workload data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const refresh = () => {
    setExpandedPM(null);
    loadData();
  };

  const setCap = async (name, dollars) => {
    // optimistic local update; the Under-Mgmt tile sums totalUnderMgmt (not caps), so summary stays correct
    setData((prev) => (prev ? { ...prev, pms: prev.pms.map((p) => (p.name === name ? { ...p, capacityCap: dollars } : p)) } : prev));
    try {
      await updatePMCapacity(name, dollars);
    } catch (err) {
      console.error('Failed to update PM cap:', err);
    }
  };

  const pms = data?.pms || [];
  // The top bar's search (lib/pageSearch): the PM's name and their projects' names + numbers.
  // The urgency tabs count the PMs it matches.
  const search = usePageSearch('Search project managers…');
  const matchesText = useCallback(
    (p) => matchesSearch(search, p.name, (p.projectBreakdown || []).flatMap((r) => [r.project_name, r.project_number])),
    [search]
  );

  const counts = useMemo(
    () => ({
      all: pms.filter(matchesText).length,
      now: pms.filter((p) => urgencyBucket(p.daysUntilFree) === 'now' && matchesText(p)).length,
      soon: pms.filter((p) => urgencyBucket(p.daysUntilFree) === 'soon' && matchesText(p)).length,
      later: pms.filter((p) => urgencyBucket(p.daysUntilFree) === 'later' && matchesText(p)).length,
    }),
    [pms, matchesText]
  );

  const visible = useMemo(() => {
    let list = pms.filter((p) => (filterUrgency === 'all' || urgencyBucket(p.daysUntilFree) === filterUrgency) && matchesText(p));
    if (sortBy === 'workload') list.sort((a, b) => b.workloadScore - a.workloadScore);
    else if (sortBy === 'soonest') list.sort((a, b) => a.daysUntilFree - b.daysUntilFree);
    else if (sortBy === 'projects') list.sort((a, b) => b.activeProjects - a.activeProjects);
    else if (sortBy === 'name') list.sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }, [pms, filterUrgency, sortBy, matchesText]);

  if (loading) return <LoadingState message="Loading workload…" />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <style>{`
        @keyframes wSpin { to { transform: rotate(360deg); } }
        /* Quiet-ledger KPI row (matches Job Costs): one neutral surface panel with
           hairline dividers on wide; below ~1120px it dissolves into a tidy neutral
           card grid (dividers drop out) so wrapping never strands a divider. */
        @media (max-width: 1120px) {
          .wkr { display: grid !important; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 10px; padding: 0 !important; background: transparent !important; border: none !important; box-shadow: none !important; }
          .wkr .wkr-div { display: none; }
          .wkr .wkr-tile { min-width: 0 !important; background: var(--bg-surface); border: 1px solid var(--border-default); border-radius: 10px; box-shadow: var(--shadow-xs); }
        }
        .w-refresh:hover { border-color: color-mix(in oklch, var(--accent) 35%, var(--border-default)); color: var(--accent-text); background: var(--accent-soft); }
        .w-cap-edit:hover { color: var(--accent-hover) !important; border-bottom-color: var(--accent) !important; }
        .w-pmhead:hover { background: color-mix(in oklch, var(--bg-hover) 45%, transparent); }
        .w-prow:hover { background: var(--bg-hover); }
        .w-prow:hover td:first-child span { color: var(--accent-text); }
        .w-sort:focus-visible { outline: none; border-color: var(--accent); box-shadow: 0 0 0 3px var(--accent-ring); }
        .w-cap-input::-webkit-outer-spin-button, .w-cap-input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
      `}</style>

      <ActionRow onRefresh={refresh} />

      {error ? (
        <StateScreen
          kind="server-error"
          title="Couldn’t load workload"
          body="Something went wrong fetching the team workload dashboard. Check your connection and try again."
          primary={{ label: 'Retry', onClick: loadData }}
        />
      ) : (
        <>
          <KpiRow summary={data?.summary || {}} />
          <FilterSortBar filterUrgency={filterUrgency} onFilter={setFilterUrgency} counts={counts} sortBy={sortBy} onSort={setSortBy} />
          {visible.length === 0 && search.trim() && pms.length > 0 ? (
            <StateScreen kind="empty" {...noMatchText(search)} />
          ) : visible.length === 0 ? (
            <StateScreen kind="empty" title="No project managers match this filter" body="Try a different urgency filter, or switch back to All to see the full team." />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {visible.map((pm) => (
                <PMCard
                  key={pm.name}
                  pm={pm}
                  open={expandedPM === pm.name}
                  onToggle={() => setExpandedPM((cur) => (cur === pm.name ? null : pm.name))}
                  onSetCap={setCap}
                  onOpenProject={onOpenProject}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default WorkloadPage;
